import React, {useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import {useAuth} from '../auth-context';
import {useClient} from '../core/client';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const FALLBACK_VALUE = 'N/A';

const pickValue = (source, keys, fallback = FALLBACK_VALUE) => {
    if (!source) return fallback;
    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }
    return fallback;
};

const buildDisplayName = (user) => {
    if (!user) return FALLBACK_VALUE;
    const first = pickValue(user, ['firstName', 'first_name', 'givenName', 'given_name', 'first', 'fist']);
    const last = pickValue(user, ['lastName', 'last_name', 'familyName', 'family_name', 'last']);
    const assembled = [first, last].filter((part) => part !== FALLBACK_VALUE).join(' ');
    if (assembled) return assembled;
    return pickValue(user, ['name', 'displayName', 'fullName', 'full_name', 'username', 'email']);
};

const InfoRow = ({label, value}) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'right' }}>
            {value}
        </Typography>
    </Box>
);

const Profile = () => {
    const { user, setUser } = useAuth();
    const client = useClient();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(user?.profilePhotoUrl || '');
    const [photoSaving, setPhotoSaving] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [photoSuccess, setPhotoSuccess] = useState('');

    const displayName = buildDisplayName(user);
    const email = pickValue(user, ['email', 'emailAddress', 'username']);
    const phone = pickValue(user, ['phone', 'phoneNumber', 'phone_number']);

    const handlePhotoFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type?.startsWith('image/')) {
            setPhotoError('Please choose an image file.');
            setPhotoSuccess('');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setProfilePhotoUrl(typeof reader.result === 'string' ? reader.result : '');
            setPhotoError('');
            setPhotoSuccess('');
        };
        reader.onerror = () => {
            setPhotoError('Unable to read image file.');
            setPhotoSuccess('');
        };
        reader.readAsDataURL(file);
    };

    const handlePhotoSave = async () => {
        if (!profilePhotoUrl) {
            setPhotoError('Choose an image first.');
            setPhotoSuccess('');
            return;
        }
        setPhotoSaving(true);
        setPhotoError('');
        setPhotoSuccess('');
        try {
            const { data } = await client.post('/auth/profile/photo', {
                photoUrl: profilePhotoUrl,
            });
            const updatedUser = data?.user || { ...(user || {}), profilePhotoUrl };
            setUser?.(updatedUser);
            localStorage.setItem('auth_user', JSON.stringify(updatedUser));
            setPhotoSuccess('Profile picture updated.');
        } catch (err) {
            setPhotoError('Unable to update profile picture. Please try again.');
        } finally {
            setPhotoSaving(false);
        }
    };

    const handlePasswordUpdate = async (event) => {
        event.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (!currentPassword || !newPassword) {
            setPasswordError('Please enter your current and new password.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }

        setPasswordLoading(true);
        try {
            await client.post('/auth/password', {
                currentPassword,
                newPassword,
            });
            setPasswordSuccess('Password updated successfully.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPasswordError('Unable to update password. Please try again.');
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                Profile
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Profile picture" />
                        <CardContent>
                            <Stack spacing={2} alignItems="flex-start">
                                <Avatar
                                    src={profilePhotoUrl || user?.profilePhotoUrl || ''}
                                    alt={displayName}
                                    sx={{ width: 96, height: 96 }}
                                >
                                    {displayName !== FALLBACK_VALUE ? String(displayName).charAt(0).toUpperCase() : 'U'}
                                </Avatar>
                                <Button variant="outlined" component="label">
                                    Choose Image
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoFileChange}
                                    />
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handlePhotoSave}
                                    disabled={photoSaving || !profilePhotoUrl}
                                >
                                    {photoSaving ? 'Saving...' : 'Save Profile Picture'}
                                </Button>
                                {photoError && (
                                    <Typography variant="body2" color="error">
                                        {photoError}
                                    </Typography>
                                )}
                                {photoSuccess && (
                                    <Typography variant="body2" color="success.main">
                                        {photoSuccess}
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Personal info" />
                        <CardContent>
                            <Stack spacing={1.5} divider={<Divider flexItem />}>
                                <InfoRow label="Name" value={displayName} />
                                <InfoRow label="Email" value={email} />
                                <InfoRow label="Phone" value={phone} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Update password" />
                        <CardContent>
                            <Box component="form" onSubmit={handlePasswordUpdate} noValidate>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Current password"
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        value={currentPassword}
                                        onChange={(event) => setCurrentPassword(event.target.value)}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                                                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                                                        edge="end"
                                                    >
                                                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="New password"
                                        type={showNewPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={newPassword}
                                        onChange={(event) => setNewPassword(event.target.value)}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                                        edge="end"
                                                    >
                                                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        label="Confirm new password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={confirmPassword}
                                        onChange={(event) => setConfirmPassword(event.target.value)}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={passwordLoading}
                                        sx={{ color: 'common.white' }}
                                    >
                                        {passwordLoading ? 'Updating...' : 'Update password'}
                                    </Button>
                                    {passwordError && (
                                        <Typography variant="body2" color="error">
                                            {passwordError}
                                        </Typography>
                                    )}
                                    {passwordSuccess && (
                                        <Typography variant="body2" color="success.main">
                                            {passwordSuccess}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader title="Need changes?" />
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                If any of this information is out of date, reach out to your administrator or support to update it.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Profile;
